import type { Styles } from '@adornis/ass/style.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getGlobalConfig, GlobalConfig } from '@adornis/bh-matching-config/entities/global-config.js';
import '@adornis/bh-matching-signup/components/matching-comp-signup-closed.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Language } from '@adornis/translation-core/languages.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, map, tap } from 'rxjs';

/**
 * @element buddy-page-signup
 */
@customElement('buddy-page-signup')
export class BuddyPageSignup extends ChemistryLitElement {
  @property({ attribute: 'page-title' }) pageTitle?: string;
  @property({ attribute: false }) userClass = new RXController<typeof BuddyUserMentee | typeof BuddyUserMentor | null>(
    this,
    null,
  );

  @property({ attribute: false }) userEntity = new RXController<Maybe<BuddyUserMentee | BuddyUserMentor>>(this, null);

  @property({ attribute: false }) customFieldMapping?: (
    field: BaseQLFieldDefinition,
    form: GenericFieldFormController<BuddyUserMentee | BuddyUserMentor>,
    translation: TranslationController<TranslationDictionaryBuddy>,
    parent?: ChemistryLitElement,
  ) => Renderable | undefined;

  @property({ attribute: false }) fieldsOverride?: (
    userClass: typeof BuddyUserMentee | typeof BuddyUserMentor,
  ) => BaseQLFieldDefinition[];

  @state() private readonly _language = new RXController(this, globalLanguageSubject);
  @state() protected readonly _translation = new TranslationController(this);
  @state() protected readonly _currentlySelectedSemester = new RXController(this, currentlySelectedTimeframe);
  /* @state() protected readonly _activeSemester = new RXController(
    this,
    TimeframeAPI.(this.institutionID.value),
  ); */
  @state() protected readonly _config = new RXController(this, getGlobalConfig()(selectionSet(() => GlobalConfig, 2)));

  @state() protected readonly _form = new GenericFieldFormController(
    this,
    combineLatest([
      this.userClass.observable,
      this._currentlySelectedSemester.observable,
      this.userEntity.observable,
    ]).pipe(
      map(([userClass, _, userEntity]) => {
        if (userEntity) {
          return userEntity as BuddyUserMentee | BuddyUserMentor;
        }
        return userClass ? new userClass({ username: A.getGloballyUniqueID() }) : null;
      }),
      tap(user => {
        if (user) user.timeframeID = this._currentlySelectedSemester.value?._id;
      }),
    ),
    {
      success: async user => {
        try {
          if (user._id) {
            await user.save();
            void XSnackbar.show(this._translation.translate('saved'));
            this.dispatchEvent(new CustomEvent('user-updated', { detail: user }));
          } else {
            await TrDialog.waitFor(this._registerUser(user));
            void XSnackbar.show(this._translation.translate('buddy', 'successfullyRegistered'));
            goTo('/signup-success');
          }
        } catch (e) {
          logger.error({ error: e }, 'Something went wrong registering the mentee.');
          void TrDialog.alert('Beim Registrieren ist etwas schief gelaufen. Bitte versuchen es später erneut.');
        }
      },
      disabledFields: { username: 1 },
    },
  );

  protected _renderFormular() {
    if (!this._currentlySelectedSemester.value)
      return html`
        <tr-section>
          <matching-comp-signup-closed></matching-comp-signup-closed>
        </tr-section>
      `;

    return html` ${this._personalDataContent()} ${this._registerButton()} `;
  }

  override render() {
    return html`
      <tr-section padding="md" space="lg">
        ${this._header()}
        ${this._currentlySelectedSemester.isLoading || this._config.isLoading
          ? html` <tr-flex h100 flex center crossaxis-center> <tr-loader></tr-loader> </tr-flex> `
          : this._currentlySelectedSemester.value
          ? this._renderFormular()
          : html` <matching-comp-signup-closed></matching-comp-signup-closed> `}
      </tr-section>
    `;
  }

  protected _header() {
    return html`
      <tr-flex space="xs">
        <tr-flex horizontal space="lg" space-between crossaxis-center>
          <x-h1> ${this.pageTitle} </x-h1>

          <tr-button
            tone="subtle"
            ${css({ marginTop: this.spacing.xxs })}
            mode="outline"
            @click=${() =>
              this._language.value === Language.GERMAN
                ? clientTranslator.selectLanguage(Language.ENGLISH)
                : clientTranslator.selectLanguage(Language.GERMAN)}

              // TODO this is need because else not every translation changes (i.e. sidebar items)
              window.location.reload();
            }}
          >

            <tr-flex horizontal space="xs" crossaxis-center>
              <tr-icon>language</tr-icon>
              <tr-text >${this._language.value.toUpperCase()}</tr-text>
              <tr-tooltip .text=${this._translation.translate('change_language')}></tr-tooltip>
            </tr-flex>
          </tr-button>
          </tr-flex>

        ${
          this._currentlySelectedSemester.value
            ? html` <tr-text tone="subtle"> Semester: ${this._currentlySelectedSemester.value.name} </tr-text> `
            : nothing
        }
      </tr-flex>
    `;
  }

  protected _personalDataContent() {
    const userClass = this.userClass.value;
    if (!userClass) return nothing;
    const fields = this.fieldsOverride?.(userClass) ?? Object.values(userClass.fields ?? {});
    return html` <tr-card ${css({ width: '100%' })}> ${this._fields(fields)} </tr-card> `;
  }

  protected _fields(fields: BaseQLFieldDefinition[]) {
    return html`
      ${fields
        .filter(field => !userFieldBlackList.includes(field.name))
        .map(field => {
          const infoTranslation = this._translation.translate('field_description', field.name);
          return (
            this.customFieldMapping?.(field, this._form, this.translation, this) ??
            html`
              ${this._form.genericField([field.name], {
                placeholderMode: 'static-floating',
                info: infoTranslation !== `field_description.${field.name}` ? infoTranslation : undefined,
              })}
            `
          );
        })}
    `;
  }

  protected _registerButton() {
    return html`
      <tr-button
        @click=${() => {
          this._form.document!.timeframeID = this._currentlySelectedSemester.value?._id;
          this._form.validate();
        }}
        ${css({ marginLeft: 'auto' })}
      >
        ${this._translation.translate('buddy', this.userEntity.value?._id ? 'save' : 'signup')}
      </tr-button>
    `;
  }

  protected async _registerUser(user: BuddyUserMentee | BuddyUserMentor) {
    if (this.userEntity?.value) {
      await user.save();
    } else {
      const timeframe = this._currentlySelectedSemester.value;
      if (!timeframe) throw new Error('There is no active timeframe selected');

      user.timeframeID = timeframe._id;
      await user.create();
    }
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          maxWidth: '100%',
          height: '100%',
          overflowY: 'auto',
        },
      } as Styles,
    ];
  }
}
